import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConstants } from '../../context/ConstantsContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import salesService from '../../services/salesService';
import tyroConfigService from '../../services/tyroConfigService';
import storeService from '../../services/storeService';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { getTyroClient, resetTyroClient } from '../../utils/tyroClient';
import { getPreferredPrinterConfig } from '../../utils/printerPreferences';
import { isNativeApp } from '../../utils/platform';
import CashDenominationPad from './CashDenominationPad';

const FALLBACK_PAYMENT_METHODS = {
  CASH: 'Cash',
  CARD: 'Card',
  TYRO: 'Tyro',
  STRIPE: 'Stripe',
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const roundToFiveCents = (amount) => {
  const totalInCents = Math.round(amount * 100);
  const remainder = totalInCents % 10;
  let roundedCents;

  if (remainder <= 2) {
    roundedCents = totalInCents - remainder;
  } else if (remainder >= 3 && remainder <= 7) {
    roundedCents = totalInCents - remainder + 5;
  } else {
    roundedCents = totalInCents - remainder + 10;
  }

  const roundedTotal = roundedCents / 100;
  const roundingAmount = roundedTotal - amount;
  return { roundedTotal, roundingAmount };
};

const toCentsString = (amount) => {
  const value = Number(amount) || 0;
  const cents = Math.round((value + Number.EPSILON) * 100);
  return String(cents);
};

const StripeCheckoutForm = ({ amount, onPaymentSuccess, onPaymentError, setProcessing, processing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { constants } = useConstants();
  const paymentMethods = constants?.PAYMENT_METHODS || FALLBACK_PAYMENT_METHODS;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);

    try {
      const { clientSecret } = await salesService.createPaymentIntent(amount);
      const cardElement = elements.getElement(CardElement);
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });
      if (error) {
        throw new Error(error.message);
      } else if (!paymentIntent || !paymentIntent.id) {
        throw new Error('Payment confirmation failed: Invalid PaymentIntent received.');
      }
      await onPaymentSuccess(paymentMethods.CARD, paymentIntent.id, { paymentSource: 'stripe', amount });
    } catch (err) {
      onPaymentError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <label className="block text-sm font-medium text-gray-700 mb-2">Enter Card Details</label>
      <CardElement options={{ style: { base: { fontSize: '16px', '::placeholder': { color: '#aab7c4' } } } }} className="p-3 border rounded-md" />

      <button type="submit" disabled={!stripe || processing} className="w-full mt-4 bg-primary text-white py-2 rounded-md font-semibold hover:bg-primary-dark disabled:bg-gray-400">
        {processing ? 'Processing...' : `Pay ${amount.toFixed(2)}`}
      </button>
    </form>
  );
};

const normalizeTyroReceipt = (copy, label) => {
  if (!copy) return '';
  if (typeof copy === 'string') return copy;
  if (Array.isArray(copy)) {
    console.debug(`[TyroReceipt] Normalizing array for ${label}`, copy);
    return copy.join('\n');
  }
  const possibleFields = [
    copy.text,
    copy.lines,
    copy.printLines,
    copy.receipt,
    copy.receiptText,
    copy.formattedReceipt,
    copy.rawReceipt,
    copy.body,
  ];
  for (const field of possibleFields) {
    if (!field) continue;
    if (typeof field === 'string') return field;
    if (Array.isArray(field)) return field.join('\n');
    if (typeof field === 'object') {
      console.debug(`[TyroReceipt] Nested object field for ${label}`, field);
    }
  }
  try {
    console.debug(`[TyroReceipt] Serializing fallback for ${label}`, copy);
    return JSON.stringify(copy, null, 2);
  } catch (err) {
    console.warn(`[TyroReceipt] Failed to stringify receipt for ${label}`, err);
    return String(copy);
  }
};

const extractTyroReceiptCopies = (payload = {}) => {
  const assign = (value, label) => normalizeTyroReceipt(value, label);
  let customerReceipt = '';
  let merchantReceipt = '';

  const addIfEmpty = (target, value, label) => {
    if (target.current || !value) return;
    target.current = assign(value, label);
  };

  const customerTarget = { current: '' };
  const merchantTarget = { current: '' };

  addIfEmpty(customerTarget, payload.customerReceipt, 'customer');
  addIfEmpty(merchantTarget, payload.merchantReceipt, 'merchant');
  addIfEmpty(customerTarget, payload.customerReceiptText, 'customer');
  addIfEmpty(merchantTarget, payload.merchantReceiptText, 'merchant');
  addIfEmpty(customerTarget, payload.customerCopy, 'customer');
  addIfEmpty(merchantTarget, payload.merchantCopy, 'merchant');

  if (Array.isArray(payload.receipts)) {
    payload.receipts.forEach((entry) => {
      const type = (entry?.type || entry?.copyType || '').toLowerCase();
      if (type.includes('customer')) {
        addIfEmpty(customerTarget, entry, 'customer');
      } else if (type.includes('merchant')) {
        addIfEmpty(merchantTarget, entry, 'merchant');
      }
    });
  }

  if (!customerTarget.current && typeof payload === 'object') {
    addIfEmpty(customerTarget, payload.customer, 'customer');
  }
  if (!merchantTarget.current && typeof payload === 'object') {
    addIfEmpty(merchantTarget, payload.merchant, 'merchant');
  }

  // Some payloads send a single receipt blob without metadata; treat it as both copies for printing
  if (!customerTarget.current && !merchantTarget.current) {
    const fallback = assign(payload, 'receipt');
    customerReceipt = fallback;
    merchantReceipt = fallback;
  } else {
    customerReceipt = customerTarget.current;
    merchantReceipt = merchantTarget.current;
  }

  return { customerReceipt, merchantReceipt };
};

const TyroCheckoutForm = ({
  amount,
  onPaymentSuccess,
  onPaymentError,
  setProcessing,
  processing,
  paymentLocked = false,
  onReceipt,
  onTyroSessionStateChange = () => {},
  onTyroTransactionComplete = () => {},
}) => {
  const { constants } = useConstants();
  const paymentMethods = constants?.PAYMENT_METHODS || FALLBACK_PAYMENT_METHODS;
  const { activeStore } = useStore();
  const { tillId, getCurrentTillForStore } = useAuth();
  const currentTillId = activeStore?._id ? getCurrentTillForStore(activeStore._id) : tillId;
  const [tyro, setTyro] = useState(null);
  const [tyroConfig, setTyroConfig] = useState(null);
  const [tyroPrompt, setTyroPrompt] = useState(null);
  const clearTyroSessionState = useCallback(() => {
    onTyroSessionStateChange(null);
  }, [onTyroSessionStateChange]);

  useEffect(() => {
    // Ensure a fresh Tyro SDK client whenever store/till context changes.
    resetTyroClient();
    setTyro(null);
    clearTyroSessionState();
    return () => {
      clearTyroSessionState();
      resetTyroClient();
    };
  }, [activeStore?._id, currentTillId, clearTyroSessionState]);

  useEffect(() => {
    let isMounted = true;
    const fetchConfigAndInitTyro = async () => {
      if (!activeStore) return;
      try {
        const config = await tyroConfigService.getTyroConfig(activeStore._id, { includeSensitive: true });
        // Always resolve tid/integrationKey from the current till; store-level keys are ignored.
        let resolvedConfig = { ...config, tid: null, integrationKey: null, tillName: null };

        if (currentTillId) {
          try {
            const storeDetails = await storeService.getStoreById(activeStore._id);
            const matchingTill = storeDetails?.tills?.find((till) => till._id === currentTillId);
            resolvedConfig = {
              ...resolvedConfig,
              tid: matchingTill?.tid || null,
              integrationKey: matchingTill?.tyroIntegrationKey || null,
              tillName: matchingTill?.name || null,
            };
          } catch (tillError) {
            console.warn('Failed to resolve till-level Tyro settings', tillError);
          }
        }
        if (!isMounted) return;
        setTyroConfig(resolvedConfig);

        if (resolvedConfig?.apiKey) {
          const siteReference =
            [activeStore?.address?.street, activeStore?.address?.suburb, activeStore?.address?.state]
              .filter(Boolean)
              .join(', ') ||
            activeStore?.name ||
            '';
          const posProductInfo = {
            posProductVendor: 'Bizessence Pty Ltd',
            posProductName: 'BoltinPOS',
            posProductVersion: '1.0.0',
            siteReference,
          };
          try {
            const localHost = String(window?.location?.hostname || '').toLowerCase();
            const forceHeadlessForLocalhost = ['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]'].includes(localHost);
            const preferHeadless = forceHeadlessForLocalhost ? true : resolvedConfig.useHeadlessMode !== false;
            const iClient = await getTyroClient({
              apiKey: resolvedConfig.apiKey,
              posProductInfo,
              preferHeadless,
            });
            if (isMounted) setTyro(iClient);
          } catch (clientError) {
            console.error(clientError);
            if (isMounted) setTyro(null);
          }
        } else {
          console.error('Tyro iClient library not loaded or API key is missing');
        }
      } catch (error) {
        console.error('Failed to fetch Tyro config', error);
      }
    };

    fetchConfigAndInitTyro();

    return () => {
      isMounted = false;
    };
  }, [activeStore, currentTillId]);

  const handlePayment = async () => {
    if (!tyro || !tyroConfig) return;
    // Fail fast if this till is not paired with Tyro.
    if (!tyroConfig.tid || !tyroConfig.integrationKey) {
      onPaymentError('Tyro terminal not paired. Select a terminal and pair it in Settings -> Tyro.');
      return;
    }
    setProcessing(true);
    onTyroSessionStateChange({
      cancel: async () => {
        if (typeof tyro?.cancelCurrentTransaction !== 'function') {
          throw new Error('Tyro terminal cancellation is not available in this session.');
        }
        tyro.cancelCurrentTransaction();
      },
    });

    const { mid, tid, integrationKey, integratedReceiptEnabled } = tyroConfig;

    const requestParams = {
      amount: toCentsString(amount),
      enableSurcharge: false,
      integratedReceipt: integratedReceiptEnabled !== false,
      mid: parseInt(mid),
      tid: parseInt(tid),
      integrationKey,
    };
    console.log('Tyro request params:', {
      mid: requestParams.mid,
      tid: requestParams.tid,
      amount: requestParams.amount,
    });
    const isHeadless = tyroConfig?.useHeadlessMode !== false;

    const transactionCallbacks = {
      questionCallback: (question, answerCallback) => {
        if (!isHeadless) {
          console.info('Tyro question (terminal UI handles):', question);
          try {
            if (typeof answerCallback === 'function') {
              const fallback =
                question?.defaultOption ||
                question?.defaultAnswer ||
                question?.options?.[0] ||
                question?.buttons?.[0] ||
                'YES';
              answerCallback(fallback);
            }
          } catch (err) {
            console.warn('Failed to auto-answer Tyro question in headful mode', err);
          }
          return;
        }
        console.info('Tyro question received', question);
        if (typeof answerCallback !== 'function') return;
        const options = Array.isArray(question?.options) && question.options.length > 0
          ? question.options
          : Array.isArray(question?.buttons) && question.buttons.length > 0
            ? question.buttons
            : ['YES', 'NO'];
        const promptText =
          question?.text ||
          question?.displayText ||
          question?.message ||
          question?.prompt ||
          question?.status ||
          'Please respond on the terminal.';
        setTyroPrompt({
          text: promptText,
          options,
          answerCallback,
          awaitingResult: false,
        });
      },
      statusMessageCallback: (message) => {
        console.info('Tyro status', message);
      },
      receiptCallback: (receipt) => {
        console.debug('[Tyro] receiptCallback payload', receipt);
        if (typeof onReceipt === 'function') {
          onReceipt(receipt);
        }
      },
      transactionCompleteCallback: (result) => {
        clearTyroSessionState();
        if (typeof onReceipt === 'function') {
          const receiptPayload = {
            customerReceipt: result?.customerReceipt,
            merchantReceipt: result?.merchantReceipt,
            receipts: result?.receipts,
            tyroTransactionId: result?.transactionId,
          };
          if (receiptPayload.customerReceipt || receiptPayload.merchantReceipt || (Array.isArray(receiptPayload.receipts) && receiptPayload.receipts.length > 0)) {
            onReceipt(receiptPayload);
          }
        }

        const finishTransaction = async () => {
          if (result.result === 'APPROVED') {
            try {
              await onPaymentSuccess(paymentMethods.TYRO, result.transactionId, {
                paymentSource: 'tyro',
                amount,
              });
            } catch (err) {
              if (!err?.tyroPaymentApproved) {
                onPaymentError(err?.message || 'Failed to finalize Tyro sale.');
              }
            }
          } else {
            onPaymentError(result.errorReason);
          }
        };

        Promise.resolve()
          .then(finishTransaction)
          .finally(() => {
            setProcessing(false);
            setTyroPrompt(null);
            onTyroTransactionComplete(result);
          });
      },
    };

    try {
      tyro.initiatePurchase(requestParams, transactionCallbacks);
    } catch (err) {
      clearTyroSessionState();
      onPaymentError(err.message);
      setProcessing(false);
      setTyroPrompt(null);
    }
  };

  const handleTyroPromptAnswer = (option) => {
    if (!tyroPrompt) return;
    try {
      tyroPrompt.answerCallback(option);
      setTyroPrompt((prev) => (prev ? { ...prev, awaitingResult: true, selectedOption: option } : null));
    } catch (err) {
      console.error('Failed to send Tyro answer', err);
      setTyroPrompt(null);
    }
  };

  if (!tyroConfig || !tyroConfig.integrationKey || !tyroConfig.tid) {
    return (
      <div className="text-center text-red-500">
        Tyro terminal not paired. Select a terminal in Terminal Setup and pair it in Settings -> Tyro.
      </div>
    );
  }

  return (
    <>
      <button onClick={handlePayment} disabled={!tyro || processing || paymentLocked} className="w-full mt-4 bg-secondary text-white py-2 rounded-md font-semibold hover:bg-secondary-dark disabled:bg-gray-400">
        {paymentLocked ? 'Tyro payment captured - save sale below' : processing ? 'Processing...' : `Pay ${amount.toFixed(2)} with Tyro`}
      </button>
      {tyroConfig?.useHeadlessMode !== false && tyroPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold">Tyro Question</h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{tyroPrompt.text}</p>
            {tyroPrompt.awaitingResult && (
              <p className="text-xs text-gray-500">
                Response sent ({tyroPrompt.selectedOption || '...'}). Waiting for terminal to continue.
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tyroPrompt.options.map((option) => (
                <button
                  key={option}
                  onClick={() => handleTyroPromptAnswer(option)}
                  disabled={tyroPrompt.awaitingResult}
                  className={`px-4 py-2 rounded-md border border-primary text-primary hover:bg-primary hover:text-white ${
                    tyroPrompt.awaitingResult ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};


const formatDenominationDisplay = (value) => {
  if (value >= 1) {
    const rounded = Number(value);
    return `$${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(2)}`;
  }
  return `${Math.round(Number(value) * 100)}¢`;
};

const roundMoney = (value) => Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;

const buildTyroSaleFinalizationMeta = ({ paymentMethods, transactionId, amount, extra }) => {
  const source = extra?.paymentSource || 'tyro';
  const hasAllocations = Array.isArray(extra?.paymentAllocations) && extra.paymentAllocations.length > 0;
  if (source !== 'tyro' || !transactionId || hasAllocations) {
    return extra;
  }

  return {
    ...extra,
    paymentSource: 'tyro',
    paymentAllocations: [
      {
        allocationId: 'alloc-1',
        method: paymentMethods.TYRO,
        source: 'tyro',
        amount: roundMoney(extra?.amount ?? amount),
        gatewayTransactionId: transactionId,
        tyroTransactionId: transactionId,
        status: 'captured',
      },
    ],
  };
};

const PaymentModal = ({ total, onCancel, onFinalizeSale, onTyroReceipt = () => {}, saleFinalizing = false }) => {
  const { constants } = useConstants();
  const paymentMethods = constants?.PAYMENT_METHODS || FALLBACK_PAYMENT_METHODS;
  const paymentConfigAvailable = Boolean(constants);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const { activeStore } = useStore();
  const { tillId, getCurrentTillForStore } = useAuth();
  const currentTillId = activeStore?._id ? getCurrentTillForStore(activeStore._id) : tillId;
  const [drawerKicked, setDrawerKicked] = useState(false);
  const [drawerWarning, setDrawerWarning] = useState('');
  const [cashTendered, setCashTendered] = useState(total);
  const [cashError, setCashError] = useState('');
  const [cardMode, setCardMode] = useState('tyro'); // tyro | stripe | manual
  const [manualCardRef, setManualCardRef] = useState('');
  const [denominationCounts, setDenominationCounts] = useState({});
  const [splitLegs, setSplitLegs] = useState([]);
  const [splitMethod, setSplitMethod] = useState(FALLBACK_PAYMENT_METHODS.CASH);
  const [splitAmount, setSplitAmount] = useState(roundMoney(total));
  const [splitCashTendered, setSplitCashTendered] = useState(roundMoney(total));
  const [splitCardMode, setSplitCardMode] = useState('manual');
  const [splitManualCardRef, setSplitManualCardRef] = useState('');
  const [cashCardCashAmount, setCashCardCashAmount] = useState(roundMoney(total));
  const [cashCardCashTendered, setCashCardCashTendered] = useState(roundMoney(total));
  const [cashCardCardMode, setCashCardCardMode] = useState('manual');
  const [cashCardManualCardRef, setCashCardManualCardRef] = useState('');
  const [cashCardLeg, setCashCardLeg] = useState(null);
  const [cashCardDenominationCounts, setCashCardDenominationCounts] = useState({});
  const [activeTyroSession, setActiveTyroSession] = useState(null);
  const [tyroCancelInFlight, setTyroCancelInFlight] = useState(false);
  const [approvedTyroFinalization, setApprovedTyroFinalization] = useState(null);
  const tyroCancelPendingCloseRef = useRef(false);
  const finalizeSaleInFlightRef = useRef(false);
  const saleProcessing = processing || saleFinalizing;
  const saleCancelLocked = saleFinalizing || (processing && !activeTyroSession?.cancel);

  const isHandheldApp = process.env.REACT_APP_APP_TARGET === 'handheld';
  const splitPaymentsEnabled = Boolean(activeStore?.splitPaymentsEnabled);
  const cashCardQuickPaymentEnabled = Boolean(activeStore?.cashCardQuickPaymentEnabled);
  const splitPaidTotal = roundMoney(
    splitLegs.reduce((sum, leg) => sum + Number(leg?.amount || 0), 0)
  );
  const splitRemaining = roundMoney(Math.max(Number(total || 0) - splitPaidTotal, 0));
  const hasCapturedSplitLeg = splitLegs.length > 0;
  const normalizedSplitAmount = roundMoney(splitAmount);
  const splitAmountExceedsRemaining = normalizedSplitAmount - splitRemaining > 0.01;
  const splitCashTenderedAmount = roundMoney(splitCashTendered);
  const splitCashChangePreview = roundMoney(Math.max(splitCashTenderedAmount - normalizedSplitAmount, 0));
  const cashCardRemaining = roundMoney(Math.max(Number(total || 0) - Number(cashCardLeg?.amount || 0), 0));
  const normalizedCashCardAmount = roundMoney(cashCardCashAmount);
  const cashCardAmountExceedsTotal = normalizedCashCardAmount - roundMoney(total) > 0.01;
  const cashCardChangePreview = roundMoney(
    Math.max(roundMoney(cashCardCashTendered) - normalizedCashCardAmount, 0)
  );
  const paymentRecoveryLocked = Boolean(approvedTyroFinalization);

  useEffect(() => {
    if (!paymentMethod) {
      setPaymentMethod('Card'); // Default to Card regardless of config load success
    }
  }, [paymentMethod]);

  useEffect(() => {
    const provider = (activeStore?.cardPaymentProvider || '').toLowerCase();
    if (provider === 'tyro') {
      setCardMode('tyro');
      setSplitCardMode('tyro');
      setCashCardCardMode('tyro');
    } else if (provider === 'stripe') {
      setCardMode('stripe');
      setSplitCardMode('stripe');
      setCashCardCardMode('stripe');
    } else {
      setCardMode('manual');
      setSplitCardMode('manual');
      setCashCardCardMode('manual');
    }
  }, [activeStore?.cardPaymentProvider]);

  useEffect(() => {
    if (paymentMethod === paymentMethods.CASH) {
      setCashTendered(roundToFiveCents(total).roundedTotal);
    } else {
      setCashTendered(total);
    }
    setDenominationCounts({});
    setCashError('');
    if (paymentMethod !== paymentMethods.CASH) {
      setDrawerWarning('');
    }
  }, [total, paymentMethod, paymentMethods.CASH]);

  useEffect(() => {
    if (paymentMethod !== 'Split') return;
    setSplitAmount((prev) => {
      const next = splitRemaining;
      if (!Number.isFinite(next)) return prev;
      return roundMoney(next);
    });
    setSplitCashTendered((prev) => {
      const next = splitRemaining;
      if (!Number.isFinite(next)) return prev;
      return roundMoney(next);
    });
  }, [paymentMethod, splitRemaining]);

  useEffect(() => {
    if (paymentMethod !== 'CashCard') return;
    if (cashCardLeg) return;
    const defaultCashAmount = roundMoney(Math.max(Number(total || 0), 0));
    setCashCardCashAmount(defaultCashAmount);
    setCashCardCashTendered(defaultCashAmount);
  }, [paymentMethod, total, cashCardLeg]);

  const handleTyroReceipt = (receipt) => {
    if (!receipt) return;
    console.debug('[Tyro] handleTyroReceipt payload', receipt);
    const extracted = extractTyroReceiptCopies(receipt);
    if (!extracted.customerReceipt && !extracted.merchantReceipt) {
      return;
    }
    if (activeStore?.tyroConfig?.integratedReceiptEnabled === false) {
      console.info('[Tyro] Integrated receipts disabled; ignoring Tyro receipt copies');
      return;
    }
    const tyroTransactionId = receipt?.tyroTransactionId || receipt?.transactionId || undefined;
    if (typeof onTyroReceipt === 'function') {
      onTyroReceipt((prevReceipts) => ({
        customerReceipt: extracted.customerReceipt || prevReceipts?.customerReceipt || '',
        merchantReceipt: extracted.merchantReceipt || prevReceipts?.merchantReceipt || '',
        copies: [
          ...(Array.isArray(prevReceipts?.copies) ? prevReceipts.copies : []),
          ...(extracted.customerReceipt || extracted.merchantReceipt
            ? [{
                customerReceipt: extracted.customerReceipt || '',
                merchantReceipt: extracted.merchantReceipt || '',
                tyroTransactionId,
              }]
            : []),
        ],
      }));
    }
  };

  const handleTyroSessionStateChange = useCallback((sessionState) => {
    setActiveTyroSession(sessionState);
    if (!sessionState) {
      setTyroCancelInFlight(false);
    }
  }, []);

  const handleTyroTransactionComplete = useCallback((result) => {
    if (!tyroCancelPendingCloseRef.current) return;
    tyroCancelPendingCloseRef.current = false;
    setTyroCancelInFlight(false);
    if (result?.result === 'APPROVED') {
      return;
    }
    onCancel();
  }, [onCancel]);

  const handleFinalizeSale = async (selectedMethod, transactionId, extra = {}) => {
    if (finalizeSaleInFlightRef.current || saleFinalizing) {
      return;
    }

    finalizeSaleInFlightRef.current = true;
    setProcessing(true);

    try {
      const normalizedExtra = selectedMethod === paymentMethods.TYRO
        ? buildTyroSaleFinalizationMeta({
            paymentMethods,
            transactionId,
            amount: total,
            extra,
          })
        : extra;
      const finalizationTransactionId =
        selectedMethod === paymentMethods.TYRO && normalizedExtra?.paymentSource === 'tyro'
          ? null
          : transactionId;

      await onFinalizeSale(selectedMethod, finalizationTransactionId, { ...normalizedExtra, tillId: currentTillId });
      if (selectedMethod === paymentMethods.TYRO && normalizedExtra?.paymentSource === 'tyro') {
        setApprovedTyroFinalization(null);
      }
    } catch (err) {
      const message = err?.message || 'Failed to process sale.';
      if (selectedMethod === paymentMethods.TYRO && (extra?.paymentSource || 'tyro') === 'tyro' && transactionId) {
        setApprovedTyroFinalization({
          transactionId,
          extra: {
            ...extra,
            amount: extra?.amount ?? total,
          },
          message,
        });
        setError(
          `Tyro payment approved (transaction ${transactionId}), but the sale was not saved: ${message} Do not take payment again. Retry saving the sale or ask a manager to reconcile this transaction.`
        );
        const recoveryError =
          err && typeof err === 'object'
            ? err
            : new Error(message);
        recoveryError.tyroPaymentApproved = true;
        throw recoveryError;
      } else {
        setError(message);
      }
    } finally {
      finalizeSaleInFlightRef.current = false;
      setProcessing(false);
    }
  };

  const handleRetryApprovedTyroFinalization = () => {
    if (!approvedTyroFinalization?.transactionId) return;
    handleFinalizeSale(
      paymentMethods.TYRO,
      approvedTyroFinalization.transactionId,
      approvedTyroFinalization.extra
    ).catch(() => {});
  };

  const cashRounding = paymentMethod === paymentMethods.CASH ? roundToFiveCents(total) : null;
  const displayTotal = paymentMethod === paymentMethods.CASH ? cashRounding.roundedTotal : total;
  const showDenominationPad = !isHandheldApp && paymentMethod === paymentMethods.CASH && activeStore?.cashDenominationPadEnabled;
  const showCashCardDenominationPad =
    !isHandheldApp &&
    paymentMethod === 'CashCard' &&
    !cashCardLeg &&
    activeStore?.cashDenominationPadEnabled;

  const handleCashPayment = () => {
    if (cashTendered < displayTotal) {
      setCashError('Cash tendered must be equal to or greater than the total.');
      return;
    }
    const changeDue = cashTendered - displayTotal;
    handleFinalizeSale(paymentMethods.CASH, null, {
      paymentSource: 'cash',
      cashTendered,
      cashChange: changeDue,
    });
  };

  const resetSplitEntryInputs = () => {
    setSplitManualCardRef('');
    setSplitAmount(splitRemaining);
    setSplitCashTendered(splitRemaining);
  };

  const resetCashCardFlow = () => {
    const defaultAmount = roundMoney(Math.max(Number(total || 0), 0));
    setCashCardCashAmount(defaultAmount);
    setCashCardCashTendered(defaultAmount);
    setCashCardManualCardRef('');
    setCashCardLeg(null);
    setCashCardDenominationCounts({});
    setError('');
    setCashError('');
  };

  const pushSplitLeg = (leg) => {
    const amount = roundMoney(leg?.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Split leg amount must be greater than 0.');
      return false;
    }
    if (amount - splitRemaining > 0.01) {
      setError('Split leg amount cannot exceed remaining balance.');
      return false;
    }
    setSplitLegs((prev) => [
      ...prev,
      {
        allocationId: `alloc-${prev.length + 1}`,
        ...leg,
        amount,
      },
    ]);
    setError('');
    setCashError('');
    return true;
  };

  const handleAddSplitCashLeg = () => {
    const amount = roundMoney(splitAmount);
    const tendered = roundMoney(splitCashTendered);
    if (amount <= 0) {
      setError('Enter a valid split amount.');
      return;
    }
    if (tendered + 0.0001 < amount) {
      setError('Cash tendered must be equal to or greater than the split amount.');
      return;
    }
    const captured = pushSplitLeg({
      amount,
      method: paymentMethods.CASH,
      source: 'cash',
      cashTendered: tendered,
      cashChange: roundMoney(tendered - amount),
      status: 'captured',
    });
    if (captured) {
      resetSplitEntryInputs();
    }
  };

  const handleAddSplitManualCardLeg = () => {
    const amount = roundMoney(splitAmount);
    if (amount <= 0) {
      setError('Enter a valid split amount.');
      return;
    }
    const captured = pushSplitLeg({
      amount,
      method: paymentMethods.CARD,
      source: 'manual',
      terminalReference: splitManualCardRef || undefined,
      status: 'captured',
    });
    if (captured) {
      resetSplitEntryInputs();
    }
  };

  const handleCaptureCashCardCashLeg = () => {
    const amount = roundMoney(cashCardCashAmount);
    const tendered = roundMoney(cashCardCashTendered);
    const totalAmount = roundMoney(total);
    if (amount <= 0) {
      setError('Cash portion must be greater than $0.00.');
      return;
    }
    if (amount >= totalAmount - 0.009) {
      setError('Cash portion must be less than total so card can pay the remainder.');
      return;
    }
    if (amount - totalAmount > 0.01) {
      setError('Cash portion cannot exceed the sale total.');
      return;
    }
    if (tendered + 0.0001 < amount) {
      setError('Cash tendered must be equal to or greater than the cash portion.');
      return;
    }
    setCashCardLeg({
      allocationId: 'alloc-1',
      method: paymentMethods.CASH,
      source: 'cash',
      amount,
      cashTendered: tendered,
      cashChange: roundMoney(tendered - amount),
      status: 'captured',
    });
    setError('');
    setCashError('');
  };

  const handleCashCardCardCapture = async (selectedMethod, transactionId, extra = {}) => {
    if (!cashCardLeg) {
      setError('Capture the cash portion first.');
      return;
    }
    const cardAmount = roundMoney(cashCardRemaining);
    if (cardAmount <= 0) {
      setError('No remaining amount to charge on card.');
      return;
    }
    const source =
      extra?.paymentSource || (selectedMethod === paymentMethods.TYRO ? 'tyro' : selectedMethod === paymentMethods.STRIPE ? 'stripe' : 'manual');
    const paymentAllocations = [
      {
        allocationId: cashCardLeg.allocationId,
        method: cashCardLeg.method,
        source: cashCardLeg.source,
        amount: roundMoney(cashCardLeg.amount),
        cashTendered: roundMoney(cashCardLeg.cashTendered || cashCardLeg.amount),
        cashChange: roundMoney(cashCardLeg.cashChange || 0),
        status: cashCardLeg.status || 'captured',
      },
      {
        allocationId: 'alloc-2',
        method: selectedMethod,
        source,
        amount: cardAmount,
        gatewayTransactionId: transactionId || undefined,
        terminalReference: extra?.terminalReference || undefined,
        stripePaymentIntentId: transactionId && source === 'stripe' ? transactionId : undefined,
        tyroTransactionId: transactionId && source === 'tyro' ? transactionId : undefined,
        status: 'captured',
      },
    ];
    await handleFinalizeSale(paymentMethods.CASH, null, {
      paymentSource: 'cash',
      paymentAllocations,
      cashTendered: roundMoney(cashCardLeg.cashTendered || cashCardLeg.amount),
      cashChange: roundMoney(cashCardLeg.cashChange || 0),
    });
  };

  const handleSplitCardCapture = (selectedMethod, transactionId, extra = {}) => {
    const amount = roundMoney(extra?.amount ?? splitAmount);
    const source = extra?.paymentSource || (selectedMethod === paymentMethods.TYRO ? 'tyro' : 'manual');
    if (amount <= 0) {
      setError('Enter a valid split amount.');
      return;
    }
    const captured = pushSplitLeg({
      amount,
      method: selectedMethod,
      source,
      gatewayTransactionId: transactionId || undefined,
      terminalReference: extra?.terminalReference || undefined,
      stripePaymentIntentId: transactionId && source === 'stripe' ? transactionId : undefined,
      tyroTransactionId: transactionId && source === 'tyro' ? transactionId : undefined,
      status: 'captured',
    });
    if (captured) {
      resetSplitEntryInputs();
    }
  };

  const handleCompleteSplitSale = async () => {
    if (splitRemaining > 0.009) {
      setError('Split payment is incomplete. Remaining amount must be $0.00.');
      return;
    }
    if (!splitLegs.length) {
      setError('Add at least one payment leg to complete the sale.');
      return;
    }
    const totalCashTendered = roundMoney(
      splitLegs.reduce((sum, leg) => sum + Number(leg?.cashTendered || 0), 0)
    );
    const totalCashChange = roundMoney(
      splitLegs.reduce((sum, leg) => sum + Number(leg?.cashChange || 0), 0)
    );
    const paymentAllocations = splitLegs.map((leg, index) => ({
      allocationId: leg?.allocationId || `alloc-${index + 1}`,
      method: leg?.method,
      source: leg?.source,
      amount: roundMoney(leg?.amount),
      surchargeAmount: roundMoney(leg?.surchargeAmount || 0),
      gatewayTransactionId: leg?.gatewayTransactionId || undefined,
      terminalReference: leg?.terminalReference || undefined,
      stripePaymentIntentId: leg?.stripePaymentIntentId || undefined,
      tyroTransactionId: leg?.tyroTransactionId || undefined,
      cashTendered: leg?.cashTendered != null ? roundMoney(leg.cashTendered) : undefined,
      cashChange: leg?.cashChange != null ? roundMoney(leg.cashChange) : undefined,
      status: leg?.status || 'captured',
    }));
    const primaryMethod = paymentAllocations[0]?.method || paymentMethods.CARD;
    const primarySource = paymentAllocations[0]?.source || 'split';
    await handleFinalizeSale(primaryMethod, null, {
      paymentSource: primarySource,
      paymentAllocations,
      ...(totalCashTendered > 0 ? { cashTendered: totalCashTendered } : {}),
      ...(totalCashChange > 0 ? { cashChange: totalCashChange } : {}),
    });
  };

  const triggerDrawerKick = async () => {
    if (!isNativeApp()) {
      return;
    }
    setDrawerWarning('');
    try {
      const plugin = window?.Capacitor?.Plugins?.ReceiptPrinter;
      if (!plugin) {
        console.debug('[PaymentModal] ReceiptPrinter plugin not available; cannot open drawer.');
        setDrawerWarning('Receipt printer not available. Cash drawer will remain closed.');
        return;
      }
      console.debug('[PaymentModal] Triggering drawer kick for cash payment');
      const payload = { jobType: 'cash' };
      const cfg = getPreferredPrinterConfig('receipt');
      if (cfg.transport === 'network' && cfg.host) {
        payload.networkHost = cfg.host;
        if (cfg.port) {
          payload.networkPort = cfg.port;
        }
      } else if (cfg.bluetoothName) {
        payload.bluetoothNameHint = cfg.bluetoothName;
      }
      await plugin.openDrawer(payload);
    } catch (err) {
      console.warn('[PaymentModal] Drawer kick failed (non-blocking):', err);
      setDrawerWarning('Unable to open the cash drawer. Ensure the receipt printer is powered on and connected.');
    }
  };

  const kickDrawerIfNeeded = () => {
    if (!drawerKicked) {
      triggerDrawerKick();
      setDrawerKicked(true);
    }
  };

  const handleAddDenomination = (value) => {
    setDenominationCounts((prev) => {
      const nextCounts = {
        ...prev,
        [value]: (prev[value] || 0) + 1,
      };
      const total = Object.entries(nextCounts).reduce(
        (sum, [denominationValue, count]) => sum + Number(denominationValue) * count,
        0
      );
      setCashTendered(Number(total.toFixed(2)));
      return nextCounts;
    });
    setCashError('');
  };

  const handleAddCashCardDenomination = (value) => {
    setCashCardDenominationCounts((prev) => {
      const nextCounts = {
        ...prev,
        [value]: (prev[value] || 0) + 1,
      };
      const totalTendered = Object.entries(nextCounts).reduce(
        (sum, [denominationValue, count]) => sum + Number(denominationValue) * count,
        0
      );
      const roundedTendered = Number(totalTendered.toFixed(2));
      const inferredCashLeg = roundMoney(Math.min(roundedTendered, Number(total || 0)));
      // In most cases denomination total equals intended cash leg, so keep both in sync.
      setCashCardCashAmount(inferredCashLeg);
      setCashCardCashTendered(roundedTendered);
      return nextCounts;
    });
    setError('');
  };

  const handleClearDenominations = () => {
    setDenominationCounts({});
    if (paymentMethod === paymentMethods.CASH) {
      setCashTendered(displayTotal);
      setCashError('');
    }
  };

  const handleClearCashCardDenominations = () => {
    setCashCardDenominationCounts({});
    setCashCardCashTendered(roundMoney(cashCardCashAmount));
  };

  const handleCancelRequest = async () => {
    if (saleCancelLocked) {
      setError('Sale is being saved. Wait for the receipt before leaving this screen.');
      return;
    }
    if (paymentMethod === 'Split' && hasCapturedSplitLeg && splitRemaining > 0.009) {
      setError(
        'A partial split payment has already been captured. Complete the payment before leaving this screen.'
      );
      return;
    }
    if (paymentMethod === 'CashCard' && cashCardLeg && cashCardRemaining > 0.009) {
      setError(
        'Cash has already been captured. Complete the remaining card payment before leaving this screen.'
      );
      return;
    }
    if (activeTyroSession?.cancel) {
      if (tyroCancelInFlight) {
        return;
      }
      setError('');
      tyroCancelPendingCloseRef.current = true;
      setTyroCancelInFlight(true);
      try {
        await activeTyroSession.cancel();
      } catch (err) {
        tyroCancelPendingCloseRef.current = false;
        setTyroCancelInFlight(false);
        setError(err?.message || 'Unable to cancel the EFTPOS transaction. Please cancel it on the terminal.');
      }
      return;
    }
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center px-2 sm:px-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xl lg:max-w-5xl max-h-[92vh] overflow-y-auto">
        <div className="flex flex-col gap-2 mb-4 lg:flex-row lg:items-baseline lg:justify-between">
          <h2 className="text-2xl font-bold">Complete Payment</h2>
          <div className="text-center lg:text-right">
            <p className="text-sm text-slate-500">Amount due</p>
            <p className="text-4xl font-bold">
              ${displayTotal.toFixed(2)}
            </p>
            {paymentMethod === paymentMethods.CASH && cashRounding?.roundingAmount !== 0 && (
              <span className="block text-xs text-slate-500 font-normal">
                (Rounding {cashRounding.roundingAmount > 0 ? '+' : ''}{cashRounding.roundingAmount.toFixed(2)})
              </span>
            )}
          </div>
        </div>
        {!paymentConfigAvailable && (
          <div className="bg-yellow-50 text-yellow-800 border border-yellow-200 rounded p-3 mb-3 text-sm">
            Payment configuration couldn’t be loaded. Default payment options are shown; please refresh settings when possible.
          </div>
        )}
        {paymentMethod === paymentMethods.CARD && (
          <p>Provider: {activeStore?.cardPaymentProvider}</p>
        )}
        {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
        {approvedTyroFinalization && (
          <div className="bg-yellow-50 text-yellow-900 border border-yellow-300 rounded p-3 mb-4 text-sm">
            <p className="font-semibold">Tyro payment captured. Sale save needs attention.</p>
            <p className="mt-1">
              Transaction ID: {approvedTyroFinalization.transactionId}. Do not start another card payment for this cart.
            </p>
            <button
              type="button"
              onClick={handleRetryApprovedTyroFinalization}
              disabled={saleProcessing}
              className="mt-3 px-4 py-2 rounded-md bg-yellow-600 text-white font-semibold hover:bg-yellow-700 disabled:bg-gray-400"
            >
              {saleProcessing ? 'Retrying...' : 'Retry saving sale'}
            </button>
          </div>
        )}
        {drawerWarning && (
          <p className="bg-yellow-50 text-yellow-800 border border-yellow-200 p-2 rounded mb-4 text-sm">
            {drawerWarning}
          </p>
        )}

        <div className="mb-4 flex flex-wrap justify-around gap-2">
          <button
            onClick={() => {
              setPaymentMethod(paymentMethods.CASH);
              resetCashCardFlow();
              kickDrawerIfNeeded();
            }}
            disabled={paymentRecoveryLocked}
            className={`px-4 py-2 rounded-md font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${
              paymentMethod === paymentMethods.CASH ? 'bg-blue-500 text-white' : 'bg-gray-200'
            }`}
          >
            Cash
          </button>
          <button
            onClick={() => {
              setPaymentMethod(paymentMethods.CARD);
              resetCashCardFlow();
            }}
            disabled={paymentRecoveryLocked}
            className={`px-4 py-2 rounded-md font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${
              paymentMethod === paymentMethods.CARD ? 'bg-blue-500 text-white' : 'bg-gray-200'
            }`}
          >
            Card
          </button>
          {cashCardQuickPaymentEnabled && (
            <button
              onClick={() => {
                setPaymentMethod('CashCard');
                resetCashCardFlow();
                kickDrawerIfNeeded();
              }}
              disabled={paymentRecoveryLocked}
              className={`px-4 py-2 rounded-md font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${
                paymentMethod === 'CashCard' ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}
            >
              Cash &amp; Card
            </button>
          )}
          {splitPaymentsEnabled && (
            <button
              onClick={() => {
                setPaymentMethod('Split');
                resetCashCardFlow();
                kickDrawerIfNeeded();
              }}
              disabled={paymentRecoveryLocked}
              className={`px-4 py-2 rounded-md font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${
                paymentMethod === 'Split' ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}
            >
              Split
            </button>
          )}
        </div>

        {paymentMethod === 'Split' && (
          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="rounded-md border border-slate-200 p-3 space-y-3">
              <div className="text-sm font-semibold text-slate-700">Step 1: Choose tender type</div>
              <div className="flex gap-2">
                <button
                  onClick={() => setSplitMethod(paymentMethods.CASH)}
                  className={`px-3 py-2 rounded-md text-sm font-semibold border ${
                    splitMethod === paymentMethods.CASH
                      ? 'bg-primary text-white border-primary'
                      : 'bg-gray-100 text-gray-700 border-gray-300'
                  }`}
                >
                  Cash
                </button>
                <button
                  onClick={() => setSplitMethod(paymentMethods.CARD)}
                  className={`px-3 py-2 rounded-md text-sm font-semibold border ${
                    splitMethod === paymentMethods.CARD
                      ? 'bg-primary text-white border-primary'
                      : 'bg-gray-100 text-gray-700 border-gray-300'
                  }`}
                >
                  Card
                </button>
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="block text-sm font-medium text-gray-700">Step 2: Enter amount for this leg</label>
                  <button
                    type="button"
                    onClick={() => {
                      setSplitAmount(splitRemaining);
                      setSplitCashTendered(splitRemaining);
                    }}
                    className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
                    disabled={splitRemaining <= 0}
                  >
                    Use remaining amount
                  </button>
                </div>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={splitAmount}
                  onChange={(e) => setSplitAmount(roundMoney(e.target.value))}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {splitAmountExceedsRemaining && (
                  <p className="mt-1 text-xs text-red-600">
                    Leg amount cannot exceed the remaining balance.
                  </p>
                )}
              </div>

              {splitMethod === paymentMethods.CASH && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Step 3: Enter cash received for this leg
                    </label>
                    <input
                      type="number"
                      min={splitAmount || 0}
                      step="0.01"
                      value={splitCashTendered}
                      onChange={(e) => setSplitCashTendered(roundMoney(e.target.value))}
                      className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Change for this leg: ${splitCashChangePreview.toFixed(2)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddSplitCashLeg}
                    disabled={saleProcessing || splitRemaining <= 0}
                    className="w-full bg-primary text-white py-3 rounded-md font-semibold hover:bg-primary-dark disabled:bg-gray-400"
                  >
                    Capture cash leg
                  </button>
                </div>
              )}

              {splitMethod === paymentMethods.CARD && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">Step 3: Capture card leg</p>
                  <div className="flex gap-2 flex-wrap">
                    {activeStore?.cardPaymentProvider === 'Tyro' && (
                      <button
                        onClick={() => setSplitCardMode('tyro')}
                        className={`px-3 py-2 rounded-md text-sm font-semibold border ${
                          splitCardMode === 'tyro'
                            ? 'bg-primary text-white border-primary'
                            : 'bg-gray-100 text-gray-700 border-gray-300'
                        }`}
                      >
                        Tyro terminal
                      </button>
                    )}
                    {activeStore?.cardPaymentProvider === 'Stripe' && (
                      <button
                        onClick={() => setSplitCardMode('stripe')}
                        className={`px-3 py-2 rounded-md text-sm font-semibold border ${
                          splitCardMode === 'stripe'
                            ? 'bg-primary text-white border-primary'
                            : 'bg-gray-100 text-gray-700 border-gray-300'
                        }`}
                      >
                        Stripe
                      </button>
                    )}
                    <button
                      onClick={() => setSplitCardMode('manual')}
                      className={`px-3 py-2 rounded-md text-sm font-semibold border ${
                        splitCardMode === 'manual'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-gray-100 text-gray-700 border-gray-300'
                      }`}
                    >
                      Manual card
                    </button>
                  </div>

                  {splitCardMode === 'stripe' && (
                    <Elements stripe={stripePromise}>
                      <StripeCheckoutForm
                        amount={splitAmount}
                        onPaymentSuccess={handleSplitCardCapture}
                        onPaymentError={setError}
                        setProcessing={setProcessing}
                        processing={saleProcessing}
                      />
                    </Elements>
                  )}
                  {splitCardMode === 'tyro' && (
                    <TyroCheckoutForm
                      amount={splitAmount}
                      onPaymentSuccess={handleSplitCardCapture}
                      onPaymentError={setError}
                      setProcessing={setProcessing}
                      processing={saleProcessing}
                      onReceipt={handleTyroReceipt}
                      onTyroSessionStateChange={handleTyroSessionStateChange}
                      onTyroTransactionComplete={handleTyroTransactionComplete}
                    />
                  )}
                  {splitCardMode === 'manual' && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Terminal reference (optional)</label>
                      <input
                        type="text"
                        value={splitManualCardRef}
                        onChange={(e) => setSplitManualCardRef(e.target.value)}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Approval code / terminal ref"
                      />
                      <button
                        type="button"
                        onClick={handleAddSplitManualCardLeg}
                        disabled={saleProcessing || splitRemaining <= 0}
                        className="w-full bg-primary text-white py-3 rounded-md font-semibold hover:bg-primary-dark disabled:bg-gray-400"
                      >
                        Capture manual card leg
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-4 lg:sticky lg:top-0 self-start">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="flex justify-between text-sm">
                  <span>Paid</span>
                  <span className="font-semibold">${splitPaidTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>Remaining</span>
                  <span className={`font-semibold ${splitRemaining > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    ${splitRemaining.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="rounded-md border border-slate-200 p-3">
                <div className="text-sm font-semibold text-slate-700 mb-2">Captured legs</div>
                {splitLegs.length === 0 && (
                  <p className="text-sm text-slate-500">No split legs captured yet.</p>
                )}
                {splitLegs.length > 0 && (
                  <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                    {splitLegs.map((leg) => (
                      <div key={leg.allocationId} className="flex items-center justify-between border rounded p-2 text-sm">
                        <div>
                          <p className="font-semibold">
                            {leg.method} • ${Number(leg.amount || 0).toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500">
                            Source: {leg.source || 'manual'}
                            {leg.terminalReference ? ` • Ref: ${leg.terminalReference}` : ''}
                          </p>
                        </div>
                        <span className="text-[11px] px-2 py-1 rounded bg-emerald-100 text-emerald-700 font-semibold">
                          Captured
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2 bg-white lg:border lg:border-slate-200 lg:p-3 lg:rounded-md">
                <button
                  type="button"
                  onClick={handleCompleteSplitSale}
                  disabled={saleProcessing || splitRemaining > 0.009}
                  className="w-full bg-primary text-white py-3 rounded-md font-semibold hover:bg-primary-dark disabled:bg-gray-400"
                >
                  Complete split payment
                </button>
                <button
                  type="button"
                  onClick={handleCancelRequest}
                  disabled={tyroCancelInFlight || saleCancelLocked}
                  className="w-full py-2 rounded-md border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {tyroCancelInFlight ? 'Cancelling terminal...' : saleCancelLocked ? 'Saving sale...' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

        {paymentMethod === 'CashCard' && (
          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="rounded-md border border-slate-200 p-3 space-y-4">
              {!cashCardLeg && (
                <div
                  className={`space-y-3 ${
                    showCashCardDenominationPad
                      ? 'lg:grid lg:grid-cols-[3fr,2fr] lg:gap-6 lg:space-y-0'
                      : ''
                  }`}
                >
                  {showCashCardDenominationPad && (
                    <div className="pr-2">
                      <CashDenominationPad
                        onAdd={handleAddCashCardDenomination}
                        counts={cashCardDenominationCounts}
                        onClear={handleClearCashCardDenominations}
                        disabled={saleProcessing}
                        showSummary={false}
                      />
                    </div>
                  )}
                  <div className="flex flex-col space-y-3">
                    <div className="text-sm font-semibold text-slate-700">Step 1: Capture cash portion</div>
                    <div>
                      <label htmlFor="cash-card-cash-amount" className="block text-sm font-medium text-gray-700 mb-1">
                        Cash amount
                      </label>
                      <input
                        id="cash-card-cash-amount"
                        type="number"
                        min="0.01"
                        max={total}
                        step="0.01"
                        value={cashCardCashAmount}
                        onChange={(e) => setCashCardCashAmount(roundMoney(e.target.value))}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      {cashCardAmountExceedsTotal && (
                        <p className="mt-1 text-xs text-red-600">Cash amount cannot exceed total.</p>
                      )}
                      {showCashCardDenominationPad && (
                        <p className="mt-1 text-xs text-slate-500">
                          Denomination taps auto-fill this amount. You can edit it if needed.
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="cash-card-cash-tendered" className="block text-sm font-medium text-gray-700 mb-1">
                        Cash tendered
                      </label>
                      <input
                        id="cash-card-cash-tendered"
                        type="number"
                        min={cashCardCashAmount || 0}
                        step="0.01"
                        value={cashCardCashTendered}
                        onChange={(e) => setCashCardCashTendered(roundMoney(e.target.value))}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        Change for cash portion: ${cashCardChangePreview.toFixed(2)}
                      </p>
                    </div>
                    {showCashCardDenominationPad &&
                      Object.values(cashCardDenominationCounts || {}).some((count) => count > 0) && (
                        <div className="p-3 bg-slate-50 border rounded-md text-xs text-slate-600 space-y-1">
                          <div className="flex justify-between font-semibold text-slate-800 text-sm">
                            <span>From denomination pad</span>
                            <span>
                              $
                              {Object.entries(cashCardDenominationCounts)
                                .reduce((sum, [value, count]) => sum + Number(value) * count, 0)
                                .toFixed(2)}
                            </span>
                          </div>
                          {Object.entries(cashCardDenominationCounts)
                            .sort((a, b) => Number(b[0]) - Number(a[0]))
                            .map(([value, count]) => (
                              <div key={value} className="flex justify-between">
                                <span>
                                  {count} × {formatDenominationDisplay(Number(value))}
                                </span>
                                <span>${(Number(value) * count).toFixed(2)}</span>
                              </div>
                            ))}
                        </div>
                      )}
                    <button
                      type="button"
                      onClick={handleCaptureCashCardCashLeg}
                      disabled={saleProcessing}
                      className="w-full bg-primary text-white py-3 rounded-md font-semibold hover:bg-primary-dark disabled:bg-gray-400"
                    >
                      Capture cash portion
                    </button>
                  </div>
                </div>
              )}

              {cashCardLeg && (
                <div className="space-y-3">
                  <div className="text-sm font-semibold text-slate-700">Step 2: Pay remaining by card</div>
                  <div className="rounded border bg-slate-50 p-3 text-sm">
                    <p className="font-semibold text-slate-800">
                      Cash captured: ${Number(cashCardLeg.amount || 0).toFixed(2)}
                    </p>
                    <p className="text-slate-600">
                      Remaining for card: ${cashCardRemaining.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {activeStore?.cardPaymentProvider === 'Tyro' && (
                      <button
                        onClick={() => setCashCardCardMode('tyro')}
                        className={`px-3 py-2 rounded-md text-sm font-semibold border ${
                          cashCardCardMode === 'tyro'
                            ? 'bg-primary text-white border-primary'
                            : 'bg-gray-100 text-gray-700 border-gray-300'
                        }`}
                      >
                        Tyro terminal
                      </button>
                    )}
                    {activeStore?.cardPaymentProvider === 'Stripe' && (
                      <button
                        onClick={() => setCashCardCardMode('stripe')}
                        className={`px-3 py-2 rounded-md text-sm font-semibold border ${
                          cashCardCardMode === 'stripe'
                            ? 'bg-primary text-white border-primary'
                            : 'bg-gray-100 text-gray-700 border-gray-300'
                        }`}
                      >
                        Stripe
                      </button>
                    )}
                    <button
                      onClick={() => setCashCardCardMode('manual')}
                      className={`px-3 py-2 rounded-md text-sm font-semibold border ${
                        cashCardCardMode === 'manual'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-gray-100 text-gray-700 border-gray-300'
                      }`}
                    >
                      Manual card
                    </button>
                  </div>

                  {cashCardCardMode === 'stripe' && (
                    <Elements stripe={stripePromise}>
                      <StripeCheckoutForm
                        amount={cashCardRemaining}
                        onPaymentSuccess={handleCashCardCardCapture}
                        onPaymentError={setError}
                        setProcessing={setProcessing}
                        processing={saleProcessing}
                      />
                    </Elements>
                  )}
                  {cashCardCardMode === 'tyro' && (
                    <TyroCheckoutForm
                      amount={cashCardRemaining}
                      onPaymentSuccess={handleCashCardCardCapture}
                      onPaymentError={setError}
                      setProcessing={setProcessing}
                      processing={saleProcessing}
                      onReceipt={handleTyroReceipt}
                      onTyroSessionStateChange={handleTyroSessionStateChange}
                      onTyroTransactionComplete={handleTyroTransactionComplete}
                    />
                  )}
                  {cashCardCardMode === 'manual' && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Terminal reference (optional)</label>
                      <input
                        type="text"
                        value={cashCardManualCardRef}
                        onChange={(e) => setCashCardManualCardRef(e.target.value)}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Approval code / terminal ref"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          handleCashCardCardCapture(paymentMethods.CARD, null, {
                            paymentSource: 'manual',
                            terminalReference: cashCardManualCardRef || undefined,
                          })
                        }
                        disabled={saleProcessing || cashCardRemaining <= 0}
                        className="w-full bg-primary text-white py-3 rounded-md font-semibold hover:bg-primary-dark disabled:bg-gray-400"
                      >
                        Record remaining card payment
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-4 lg:sticky lg:top-0 self-start">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Cash portion</span>
                  <span className="font-semibold">
                    ${Number(cashCardLeg?.amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Card remaining</span>
                  <span className={`font-semibold ${cashCardRemaining > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    ${cashCardRemaining.toFixed(2)}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCancelRequest}
                disabled={tyroCancelInFlight || saleCancelLocked}
                className="w-full py-2 rounded-md border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {tyroCancelInFlight ? 'Cancelling terminal...' : saleCancelLocked ? 'Saving sale...' : 'Cancel'}
              </button>
            </div>
          </div>
        )}

        {paymentMethod === paymentMethods.CARD && activeStore.cardPaymentProvider === 'Tyro' && (
          <div className="mb-3 flex gap-2">
            <button
              onClick={() => setCardMode('tyro')}
              disabled={paymentRecoveryLocked}
              className={`px-3 py-2 rounded-md text-sm font-semibold border disabled:cursor-not-allowed disabled:opacity-60 ${cardMode === 'tyro' ? 'bg-primary text-white border-primary' : 'bg-gray-100 text-gray-700 border-gray-300'}`}
            >
              Tyro terminal
            </button>
            <button
              onClick={() => setCardMode('manual')}
              disabled={paymentRecoveryLocked}
              className={`px-3 py-2 rounded-md text-sm font-semibold border disabled:cursor-not-allowed disabled:opacity-60 ${cardMode === 'manual' ? 'bg-primary text-white border-primary' : 'bg-gray-100 text-gray-700 border-gray-300'}`}
            >
              Record manual card
            </button>
          </div>
        )}

        {paymentMethod === paymentMethods.CARD && activeStore.cardPaymentProvider === 'Stripe' && (
          <Elements stripe={stripePromise}>
            <StripeCheckoutForm amount={total} onPaymentSuccess={handleFinalizeSale} onPaymentError={setError} setProcessing={setProcessing} processing={saleProcessing} />
          </Elements>
        )}
        {paymentMethod === paymentMethods.CARD && activeStore.cardPaymentProvider === 'Tyro' && cardMode === 'tyro' && (
          <TyroCheckoutForm
            amount={total}
            onPaymentSuccess={handleFinalizeSale}
            onPaymentError={setError}
            setProcessing={setProcessing}
            processing={saleProcessing}
            paymentLocked={Boolean(approvedTyroFinalization)}
            onReceipt={handleTyroReceipt}
            onTyroSessionStateChange={handleTyroSessionStateChange}
            onTyroTransactionComplete={handleTyroTransactionComplete}
          />
        )}
        {paymentMethod === paymentMethods.CARD && cardMode === 'manual' && (
          <div className="mt-4 space-y-3">
            <div className="text-sm text-slate-600">
              Process the payment on the EFTPOS terminal, then record it here.
            </div>
            <label className="block text-sm font-medium text-gray-700">Terminal reference (optional)</label>
            <input
              type="text"
              value={manualCardRef}
              onChange={(e) => setManualCardRef(e.target.value)}
              className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Approval code / terminal ref"
            />
            <button
              type="button"
              onClick={() =>
                handleFinalizeSale(paymentMethods.CARD, null, {
                  paymentSource: 'manual',
                  terminalReference: manualCardRef || undefined,
                })
              }
              disabled={saleProcessing}
              className="w-full bg-primary text-white py-3 rounded-md font-semibold hover:bg-primary-dark disabled:bg-gray-400"
            >
              {saleProcessing ? 'Saving...' : 'Record manual card payment'}
            </button>
          </div>
        )}
        {paymentMethod === paymentMethods.CASH && (
          <div
            className={`mt-4 space-y-3 ${
              showDenominationPad
                ? 'lg:grid lg:grid-cols-[3fr,2fr] lg:gap-6 lg:space-y-0'
                : ''
            }`}
          >
            {showDenominationPad && (
              <div className="pr-2">
                <CashDenominationPad
                  onAdd={handleAddDenomination}
                  counts={denominationCounts}
                  onClear={handleClearDenominations}
                  disabled={saleProcessing}
                  showSummary={false}
                />
              </div>
            )}
            <div className="flex flex-col space-y-3">
              {showDenominationPad && Object.values(denominationCounts || {}).some((count) => count > 0) && (
                <div className="p-3 bg-slate-50 border rounded-md text-xs text-slate-600 space-y-1">
                  <div className="flex justify-between font-semibold text-slate-800 text-sm">
                    <span>From denomination pad</span>
                    <span>
                      $
                      {Object.entries(denominationCounts)
                        .reduce((sum, [value, count]) => sum + Number(value) * count, 0)
                        .toFixed(2)}
                    </span>
                  </div>
                  {Object.entries(denominationCounts)
                    .sort((a, b) => Number(b[0]) - Number(a[0]))
                    .map(([value, count]) => (
                      <div key={value} className="flex justify-between">
                        <span>
                          {count} × {formatDenominationDisplay(Number(value))}
                        </span>
                        <span>${(Number(value) * count).toFixed(2)}</span>
                      </div>
                    ))}
                </div>
              )}
              <label className="block text-sm font-medium text-gray-700">
                Cash tendered amount
              </label>
              <input
                type="number"
                min={displayTotal}
                step="0.01"
                value={cashTendered}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setCashTendered('');
                  } else {
                    const parsed = Number(raw);
                    if (!Number.isNaN(parsed)) {
                      setCashTendered(parsed);
                    }
                  }
                  setDenominationCounts({});
                  setCashError('');
                }}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {cashError && <p className="text-xs text-red-600">{cashError}</p>}
              {cashTendered >= displayTotal && (
                <div className="p-3 bg-slate-50 border rounded-md text-sm flex justify-between">
                  <span>Change due</span>
                  <span className="font-semibold">${(cashTendered - displayTotal).toFixed(2)}</span>
                </div>
              )}
              <button
                type="button"
                onClick={handleCashPayment}
                disabled={saleCancelLocked}
                className="w-full bg-primary text-white py-3 rounded-md font-semibold hover:bg-primary-dark disabled:bg-gray-400"
              >
                {saleProcessing ? 'Processing...' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={handleCancelRequest}
                disabled={saleProcessing}
                className="w-full mt-2 py-2 rounded-md border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saleCancelLocked ? 'Saving sale...' : 'Cancel'}
              </button>
            </div>
          </div>
        )}

        {paymentMethod !== paymentMethods.CASH && paymentMethod !== 'Split' && paymentMethod !== 'CashCard' && (
          <button
            onClick={handleCancelRequest}
            disabled={tyroCancelInFlight || saleCancelLocked}
            className="w-full mt-2 py-2 rounded-md border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {tyroCancelInFlight ? 'Cancelling terminal...' : saleCancelLocked ? 'Saving sale...' : 'Cancel'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;
